<template>
  <v-parallax class="hero" src="/img/hero-room.webp" alt="Jasmine Therapies treatment room">
    <div class="hero-banner fill-height d-flex flex-column justify-center">
      <div class="intro-text d-flex flex-column justify-center pt-0 pa-8">
        <div class="text-center text-subtitle-1 pa-2 animate-text">you are one step away from</div>
        <div class="text-center text-h2 pa-2 animate-title">complete wellness</div>
      </div>
      <div class="text-center">
        <v-btn color="primary" :href="bookingUrl" target="_blank" size="large" class="book-now-button animate-button" elevation="4">Book now!</v-btn>
      </div>
    </div>
  </v-parallax>
  
  <div class="welcome content d-flex flex-column justify-center align-center pa-8">
    <div class="text-h4 text-center mb-8 welcome-title">
      Welcome to our wellness centre!
    </div>
    <div class="text-body-1 text-center welcome-description" style="max-width: 900px;">
      Whether you're looking for pain relief, sports recovery, beauty care, or a moment to unwind, we've got you covered with massage and beauty services. Pilates and yoga are coming soon as our space grows.
    </div>
  </div>
  
  <div class="health-providers content d-flex flex-column justify-center align-center pa-8">
    <div class="text-h5 text-center">
      We are recognized by the main health providers:
    </div>
    <div class="d-flex flex-row flex-wrap ga-16 justify-space-around align-center pt-8 health-providers-logos">
      <v-img src="/img/irish-life-health.svg" alt="Irish Life Health" class="provider-logo ma-2" width="190" height="80" />
      <v-img src="/img/laya-logo.jpg" alt="Laya Healthcare" class="provider-logo ma-2" width="190" height="80" />
      <v-img class="vhi-logo provider-logo ma-2" src="/img/Vhi_Healthcare_New_Logo-p-500.png" alt="VHI Healthcare" width="170" height="80" />
    </div>
  </div>

  <section id="our-team">
    <div class="full-width-header">Our Team</div>
    <div class="content team-list">
      <div class="team-desktop d-none d-md-flex flex-column ga-10">
        <v-row
          v-for="teamMember in teamMembers"
          :key="teamMember.name"
          class="team-row"
          align="center"
          no-gutters>
          <v-col cols="12" md="6" class="pr-md-2 d-flex justify-end">
            <v-img
              :src="teamMember.image"
              :alt="teamMember.alt"
              class="team-image"
              cover />
          </v-col>
          <v-col cols="12" md="6" class="pt-4 pt-md-0">
            <div class="team-text text-center">
              <div class="team-name text-h4">
                {{ teamMember.name }}
              </div>
              <div class="team-title text-subtitle-1">
                {{ teamMember.title }}
              </div>
            </div>
          </v-col>
        </v-row>
      </div>

      <div class="team-mobile d-flex d-md-none flex-column ga-12">
        <v-parallax
          v-for="teamMember in teamMembers"
          :key="teamMember.name"
          :src="teamMember.image"
          :alt="teamMember.name + ' - Jasmine Therapies team member'"
          class="team-parallax">
          <div class="team-parallax-overlay fill-height d-flex align-center justify-center">
            <div class="team-text text-center">
              <div class="team-name text-h4">
                {{ teamMember.name }}
              </div>
              <div class="team-title text-subtitle-1">
                {{ teamMember.title }}
              </div>
            </div>
          </div>
        </v-parallax>
      </div>
    </div>
  </section>
  
  <section id="our-services">
    <div class="full-width-header">Our Services</div>
    <div class="service-list content d-flex flex-wrap justify-space-around ga-12 px-8">
      <ServiceItemComponent
        v-for="serviceItem in serviceList"
        :key="serviceItem.title"
        :title="serviceItem.title"
        :book-now-link="serviceItem.bookNowLink"
        :image-source="serviceItem.imageSource"
        :sub-title="serviceItem.subTitle"
        :button-text="serviceItem.buttonText"
        :link-target="serviceItem.linkTarget"
        :read-more-link="serviceItem.readMoreLink"
        :is-new="serviceItem.isNew"
        class="service-item-card"></ServiceItemComponent>
    </div>
  </section>

  <section id="gift-vouchers">
    <div class="full-width-header">Gift Vouchers</div>
    <div class="content d-flex flex-column align-center pb-16">
      <p class="content-intro">
        Looking for the perfect gift? Treat someone special to a Jasmine Puozzo wellness voucher.
        They choose the treatment — you give the joy.
      </p>

      <div class="voucher-highlights d-flex flex-wrap justify-center ga-6 mb-10">
        <div class="voucher-chip" v-for="amount in ['€50', '€100', '€150', '€250']" :key="amount">
          {{ amount }}
        </div>
      </div>

      <v-btn color="primary" href="/gift-vouchers" size="large" class="book-now-button">View Vouchers</v-btn>
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { BookingUrls } from '@/constants/booking';
import ServiceItemComponent from '@/components/ServiceItemComponent.vue';

export default defineComponent({
  name: 'HomeView',

  components: {
    ServiceItemComponent
  },

  data() {
    return {
      bookingUrl: BookingUrls.Default,
      serviceList: [
        {
          title: 'Beauty & Skincare',
          bookNowLink: '/services/beauty-skincare',
          subTitle: '',
          imageSource: '/img/beauty-treatment-square.webp',
          readMoreLink: null as string | null,
          buttonText: 'View details',
          linkTarget: '_self',
          isNew: true
        },
        {
          title: 'Head & Neck Massage',
          bookNowLink: '/services/head-and-neck-massage',
          subTitle: '',
          imageSource: '/img/neck-massage-square.webp',
          readMoreLink: null as string | null,
          buttonText: 'View details',
          linkTarget: '_self',
          isNew: false
        },
        {
          title: 'Lymphatic Drainage',
          bookNowLink: '/services/lymphatic-drainage',
          subTitle: '',
          imageSource: '/img/lymphatic/hero.webp',
          readMoreLink: null as string | null,
          buttonText: 'View details',
          linkTarget: '_self',
          isNew: false
        },
        {
          title: 'Pain Relief Massage',
          bookNowLink: '/services/pain-relief-massage',
          subTitle: '',
          imageSource: '/img/vacuum-cups-square.webp',
          readMoreLink: null as string | null,
          buttonText: 'View details',
          linkTarget: '_self',
          isNew: false
        },
        {
          title: 'Pregnancy Massage',
          bookNowLink: '/services/pregnancy-massage',
          subTitle: '',
          imageSource: '/img/pregnancy-square.webp',
          readMoreLink: null as string | null,
          buttonText: 'View details',
          linkTarget: '_self',
          isNew: false
        },
        {
          title: 'Reflexology',
          bookNowLink: '/services/reflexology',
          subTitle: '',
          imageSource: '/img/reflexology.webp',
          readMoreLink: null as string | null,
          buttonText: 'View details',
          linkTarget: '_self',
          isNew: false
        },
        {
          title: 'Relaxing Massage',
          bookNowLink: '/services/relaxing-massage',
          subTitle: '',
          imageSource: '/img/woman-relaxing-square.webp',
          readMoreLink: null as string | null,
          buttonText: 'View details',
          linkTarget: '_self',
          isNew: false
        },
        {
          title: 'Sports Massage',
          bookNowLink: '/services/sports-massage',
          subTitle: '',
          imageSource: '/img/golf_player.webp',
          readMoreLink: null as string | null,
          buttonText: 'View details',
          linkTarget: '_self',
          isNew: false
        },
      ],
      teamMembers: [
        {
          name: 'Jasmine Puozzo',
          title: 'Massage Therapist & Reflexologist',
          image: '/img/team/jasmine.webp',
          alt: 'Jasmine Puozzo, Massage Therapist & Reflexologist'
        },
        {
          name: 'Bianca McCall',
          title: 'Beauty Specialist',
          image: '/img/team/bianca.webp',
          alt: 'Bianca McCall, Beauty Specialist'
        }
      ]
    };
  }
});
</script>

<style scoped lang="scss">
@use '@/styles/typography' as theme;

.welcome, .health-providers {
  min-height: 300px;
  padding: 3rem 1rem;
}

.animate-text, .animate-title, .animate-button {
  animation-duration: 1.5s;
  animation-fill-mode: both;
}

.animate-text {
  animation-name: fadeInDown;
  animation-delay: 0.3s;
}

.animate-title {
  animation-name: fadeInUp;
  animation-delay: 0.6s;
}

.animate-button {
  animation-name: fadeIn;
  animation-delay: 1s;
  transition: transform 0.3s ease;
}

.animate-button:hover {
  transform: scale(1.05);
}

.welcome-title, .welcome-description {
  animation: fadeIn 1s ease-in-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translate3d(0, -30px, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translate3d(0, 30px, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
}

.team-list {
  padding: 48px;
}

.team-row + .team-row {
  margin-top: 32px;
}

.team-image {
  aspect-ratio: 1 / 1;
  max-height: 500px;
  max-width: 500px;
  width: 100%;
}

.team-image :deep(.v-img__img) {
  border-radius: 12px;
  object-position: right center;
}

.team-text {
  max-width: 500px;
}

.team-name {
  font-family: theme.$name-font;
  font-size: 36px !important;
  letter-spacing: 0.5px;
}

.team-title {
  opacity: 0.85;
}

.team-parallax {
  aspect-ratio: 1 / 1;
  border-radius: 14px;
  overflow: hidden;
  width: calc(100% - 30px);
  margin: 0 auto;
}

.team-parallax-overlay {
  align-items: center !important;
  justify-content: center !important;
  padding: 18px;
  color: rgb(var(--v-theme-background));
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.55);
  background: rgba(black, 0.6);
}

.team-parallax-overlay .team-name {
  color: rgb(var(--v-theme-background));
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.6);
}

.team-parallax-overlay .team-title {
  color: rgba(var(--v-theme-background), 0.96);
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.58);
}

.service-item-card {
  transition: box-shadow 0.3s ease;
}

.service-item-card:hover {
  box-shadow: 0 10px 20px rgba(0,0,0,0.4);
}

.health-providers-logos {
  width: 100%;
  max-width: 900px;
}

.provider-logo {
  width: 190px;
  max-height: 80px;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease;
}

.provider-logo :deep(.v-img__img) {
  object-fit: contain;
}

.provider-logo:hover {
  transform: scale(1.05);
}

.vhi-logo {
  width: 170px;
  max-width: 170px;
  background-color: #fff;
  padding: 6px 10px;
}

@media (max-width: 600px) {  
  .welcome-title
  {
    padding: 32px 0;
  }

  .team-list {
    padding: 32px;
  }

  .team-title {
    font-size: 1rem !important;
  }

  .provider-logo {
    width: 150px;
    max-height: 64px;
  }

  .vhi-logo {
    width: 150px;
    max-width: 150px;
    padding: 6px 10px;
  }
}

.voucher-chip {
  font-size: 1.5rem;
  font-weight: 700;
  color: rgb(var(--v-theme-primary));
  padding: 14px 32px;
  border: 2px solid rgb(var(--v-theme-primary));
  border-radius: 16px;
  background-color: rgba(var(--v-theme-primary), 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.voucher-chip:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25);
}
</style>
